import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { logger } from '@src/utils/misc/logger';
import { LOGIN_START } from '@utils/sensor/track';
import { trackEvent } from '@pulse/nav';
// Lightweight axios instance for auth operations - no interceptors that would
// redirect on 401 or add headers that require customer context
const authClient = axios.create({
  baseURL: `${BUILD_VARS.QUERY_DOMAIN}/v1.0`,
  withCredentials: true,
  timeout: 30_000,
});
import { Spin } from 'antd';
import LoginScreenUrl from '@images/login-screen.svg?url';
import LogoUrl from '@images/Conviva-Logo-Full.png';
import {
  GlobalStyle,
  Page,
  Background,
  Overlay,
  CardWrap,
  Card,
  LogoImage,
  SpinnerWrap,
  Title,
  Field,
  EmailInput,
  HelpLink,
  ErrorBox,
  Button,
  Footer,
  FooterPadding,
  FooterLinks,
  NoStyleLink,
} from './Login.styles';
// import { Link } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already authenticated using React Query
  const { isLoading: isCheckingAuth, data: isAuthenticated } = useQuery({
    queryKey: ['auth-check'],
    queryFn: async () => {
      try {
        await authClient.get('/customerCheck');
        const { search } = window.location;
        const params = new URLSearchParams(search);
        const next = params.get('next') || '/app';

        logger.log('User already authenticated, redirecting to:', next);
        window.location.replace(next);
        return true;
      } catch (err) {
        if ((err as any)?.response?.status !== 401) {
          logger.error('Unexpected error checking auth status:', err);
        }
        return false;
      }
    },
    retry: false,
    refetchOnWindowFocus: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { search } = window.location;
      const params = new URLSearchParams(search);
      const next = params.get('next') || '/app';

      logger.log('Initiating WebFinger login for email:', email);
      const loginTimestamp = Date.now();
      trackEvent({
        name: LOGIN_START,
        data: { useremail: email },
      });
      sessionStorage.setItem('login_pending', JSON.stringify({ email, timestamp: loginTimestamp }));

      const response = await authClient.post('/auth/webfinger', { email, next });

      if (response.data?.success && response.data?.redirectUrl) {
        window.location.href = response.data.redirectUrl;
        return;
      }

      logger.error('Unexpected WebFinger response format:', response.data);
      setError('Unable to start sign-in. Please try again.');
    } catch (err: unknown) {
      sessionStorage.removeItem('login_pending');
      logger.error('WebFinger error:', err);
      setError(
        (err as any)?.response?.data?.message || 'Unable to start sign-in. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <GlobalStyle />
      <Background id="login-bg-image-id" $bg={LoginScreenUrl} />
      <Overlay />

      <CardWrap>
        <Card>
          <LogoImage src={LogoUrl} alt="Conviva" />
          {isCheckingAuth || isAuthenticated ? (
            <SpinnerWrap>
              <Spin />
            </SpinnerWrap>
          ) : (
            <>
              <Title>Sign In</Title>
              <form onSubmit={handleSubmit}>
                <Field>
                  <EmailInput
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Field>

                {error && <ErrorBox>{error}</ErrorBox>}

                <Button type="submit" disabled={loading}>
                  {loading ? 'Continuing...' : 'Continue'}
                </Button>

                {/* <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <Link to="/auth/forgot-password" style={{ color: '#93c90e', textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </div> */}

                <HelpLink href="mailto:support@conviva.com">Help</HelpLink>
              </form>
            </>
          )}
        </Card>
      </CardWrap>

      <Footer>
        <FooterLinks>
          <FooterPadding>
            <NoStyleLink href="http://www.conviva.ai/" target="_blank" rel="noreferrer">
              About Us
            </NoStyleLink>
            <span>|</span>
            <NoStyleLink
              href="http://www.conviva.ai/contact-conviva/"
              target="_blank"
              rel="noreferrer"
            >
              Contact Us
            </NoStyleLink>
            <span>|</span>
            <NoStyleLink
              href="https://pulse.conviva.com/legal/#terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Use
            </NoStyleLink>
          </FooterPadding>

          <FooterPadding>
            See and Understand Consumer Patterns That Drive Predictable Outcomes
          </FooterPadding>
          <FooterPadding>© 2026 Conviva. All rights reserved.</FooterPadding>
        </FooterLinks>
      </Footer>
    </Page>
  );
}
